"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { ArrowRight, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  BLOG_CATEGORIES,
  postHref,
  type BlogPost,
  type BlogCategory,
} from "@/lib/blog";

const PAGE_SIZE = 9;

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Category pill ─────────────────────────────────────────────────────────────

function CategoryPill({ category }: { category: BlogCategory }) {
  return (
    <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-primary-800 shadow-[0_2px_8px_rgba(14,58,138,0.08)] backdrop-blur-sm">
      {category}
    </span>
  );
}

// ── Featured post ─────────────────────────────────────────────────────────────

function FeaturedCard({ post }: { post: BlogPost }) {
  return (
    <motion.a
      variants={fadeUp(false)}
      href={postHref(post)}
      aria-label={post.title}
      className="group grid overflow-hidden rounded-card border border-[#e3ecfb] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(10,45,110,0.35)] lg:grid-cols-2"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden lg:aspect-auto lg:h-full lg:min-h-[320px]">
        <Image
          src={post.image}
          alt={post.title}
          fill
          sizes="(min-width: 1024px) 50vw, 100vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 via-transparent to-transparent" />
        <span className="absolute left-4 top-4">
          <CategoryPill category={post.category} />
        </span>
      </div>

      <div className="flex flex-col justify-center gap-4 p-6 sm:p-8 lg:p-10">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-500">
          Featured
        </span>
        <h3 className="text-[clamp(1.4rem,2.4vw,2rem)] font-bold leading-[1.15] tracking-[-0.015em] text-primary-900">
          {post.title}
        </h3>
        <p className="text-base leading-relaxed text-text-body">
          {post.excerpt}
        </p>
        <div className="mt-1 flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
            <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
            {post.date}
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors group-hover:text-primary-800">
            Read article
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </motion.a>
  );
}

// ── Blog card ─────────────────────────────────────────────────────────────────

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <motion.a
      variants={fadeUp(false)}
      href={postHref(post)}
      aria-label={post.title}
      className="group flex flex-col overflow-hidden rounded-card border border-[#e3ecfb] bg-white transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_18px_40px_-24px_rgba(10,45,110,0.28)]"
    >
      <div className="relative h-48 w-full overflow-hidden">
        <Image
          src={post.image}
          alt={post.title}
          fill
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/35 via-primary-900/5 to-transparent" />
        <span className="absolute left-4 top-4">
          <CategoryPill category={post.category} />
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="line-clamp-2 text-lg font-bold leading-snug tracking-[-0.01em] text-primary-900">
          {post.title}
        </h3>
        <p className="line-clamp-2 text-sm leading-relaxed text-text-body">
          {post.excerpt}
        </p>
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-[#e3ecfb] pt-4">
          <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
            <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
            {post.date}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 transition-colors group-hover:text-primary-800">
            Read
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </motion.a>
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

type Filter = "All" | BlogCategory;

export function BlogIndex({ posts }: { posts: BlogPost[] }) {
  const reduce = useReducedMotion() ?? false;
  const [filter, setFilter] = useState<Filter>("All");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const featured = posts[0];

  const gridPosts = useMemo(() => {
    if (filter === "All") return posts.slice(1);
    return posts.filter((p) => p.category === filter);
  }, [filter, posts]);

  const shown = gridPosts.slice(0, visible);
  const hasMore = visible < gridPosts.length;

  const filters: Filter[] = ["All", ...BLOG_CATEGORIES];

  function selectFilter(next: Filter) {
    setFilter(next);
    setVisible(PAGE_SIZE);
  }

  return (
    <section className="bg-neutral-0" aria-labelledby="blog-heading">
      <div className="mx-auto max-w-[1200px] px-5 pb-16 pt-14 sm:px-8 sm:pb-20 sm:pt-16 lg:pb-24 lg:pt-20">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={container}
          className="max-w-[720px]"
        >
          <motion.p
            variants={fadeUp(reduce)}
            className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500"
          >
            The JustGold Blog
          </motion.p>
          <motion.h1
            id="blog-heading"
            variants={fadeUp(reduce)}
            className="text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.12] tracking-[-0.015em] text-primary-900"
          >
            Gold, markets, and money.
          </motion.h1>
          <motion.p
            variants={fadeUp(reduce)}
            className="mt-4 text-lg leading-relaxed text-text-body"
          >
            Clear insights on gold, digital ownership, and the forces moving
            markets. Written to help savers across the UAE make confident
            decisions.
          </motion.p>
        </motion.div>

        {/* Featured — only on the unfiltered view */}
        {filter === "All" && featured ? (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={container}
            className="mt-10 sm:mt-12"
          >
            <FeaturedCard post={featured} />
          </motion.div>
        ) : null}

        {/* Filters */}
        <div
          className="mt-12 flex flex-wrap gap-2.5 sm:mt-14"
          role="group"
          aria-label="Filter articles by category"
        >
          {filters.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => selectFilter(f)}
                aria-pressed={active}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40",
                  active
                    ? "border-primary-600 bg-primary-600 text-white"
                    : "border-[#e3ecfb] bg-white text-primary-700 hover:border-primary-200 hover:bg-primary-50/70"
                )}
              >
                {f}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <motion.div
          key={filter}
          initial="hidden"
          animate="visible"
          variants={container}
          className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {shown.map((post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </motion.div>

        {/* Load more */}
        {hasMore ? (
          <div className="mt-12 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="inline-flex items-center gap-2 rounded-lg border border-primary-200 bg-white px-6 py-3 text-sm font-semibold text-primary-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:bg-primary-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
            >
              Load more articles
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
